import type { AuthorshipModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatBoolean, formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface AuthorshipsTableProps {
  authorships: AuthorshipModel[];
  isLoading: boolean;
  error: Error | null;
  onSelectAuthorship: (authorshipId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const AuthorshipsTable = ({
  authorships,
  isLoading,
  error,
  onSelectAuthorship,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: AuthorshipsTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Titel</th>
        <th>Forlag</th>
        <th>Udgivelsesår</th>
        <th>ISBN</th>
        <th>Lektioner</th>
        <th>Oprettet</th>
        <th>Rapporteret</th>
        <th>Status</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={9} className="text-muted">
              Henter forfatterskab ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={9}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente forfatterskab."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          authorships.map((authorship) => (
            <tr
              key={authorship.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectAuthorship(authorship.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectAuthorship(authorship.id);
                }
              }}
            >
              <td>{authorship.bookTitle}</td>
              <td>{authorship.publisher}</td>
              <td>{authorship.publicationYear}</td>
              <td>{authorship.isbn ?? "-"}</td>
              <td>{authorship.numberOfLessons}</td>
              <td>{formatDate(authorship.createdAt)}</td>
              <td>{formatBoolean(authorship.reported)}</td>
              <td>
                {authorship.status ? (
                  <StatusBadge
                    status={formatEvidenceStatus(authorship.status)}
                    variant={getEvidenceStatusVariant(authorship.status)}
                  />
                ) : (
                  "-"
                )}
              </td>
              <td>
                <button
                  id={`efteruddannelse-authorship-${authorship.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectAuthorship(authorship.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !authorships.length && (
          <tr>
            <td colSpan={9} className="text-muted">
              Ingen forfatterskab
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
